"use client";

import Link from "next/link";
import Image from "next/image";
import { useSearchParams, usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Train, Search, MapPin, Briefcase, Clock, Wallet, ArrowRight,
  Shield, Users, TrendingUp, Award, ChevronRight, ChevronLeft, Filter, Building2,
  GraduationCap, CheckCircle2, FileText, UserPlus, Send, Bell,
} from "lucide-react";
import heroImage from "@/assets/metro-hero.jpg";
import { PortalHeader, PortalFooter } from "@/components/portal-chrome";
import { getStoredCandidate } from "@/lib/auth";
import { useAppliedJobs } from "@/lib/use-applied-jobs";
import { getLocalizedJob, type Job } from "@/lib/jobs";
import { useLanguage } from "@/lib/i18n/LanguageContext";

function splitLocations(location: string): string[] {
  return location.split(",").map((l) => l.trim()).filter(Boolean);
}

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

function readInitialParams(searchParams: URLSearchParams) {
  return typeof window === "undefined" ? searchParams : new URLSearchParams(window.location.search);
}

export function HomeClient({ jobs }: { jobs: Job[] }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t, lang } = useLanguage();

  const localizedJobs = useMemo(
    () => jobs.map((j) => getLocalizedJob(j, lang)),
    [jobs, lang],
  );

  const [dept, setDept] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [location, setLocation] = useState("ALL");
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(() => {
    const fromUrl = Number(readInitialParams(searchParams).get("limit"));
    return PAGE_SIZE_OPTIONS.includes(fromUrl) ? fromUrl : DEFAULT_PAGE_SIZE;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    const fromUrl = Number(readInitialParams(searchParams).get("page"));
    return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
  });
  const { appliedIds, checking: checkingApplied } = useAppliedJobs();

  const departments = useMemo(
    () => [
      { id: "ALL", label: t.home.allDepartments },
      ...Array.from(new Set(localizedJobs.map((j) => j.dept))).map((d) => ({ id: d, label: d })),
    ],
    [localizedJobs, t.home.allDepartments],
  );

  const types = useMemo(
    () => [
      { id: "ALL", label: t.home.anyType },
      ...Array.from(new Set(localizedJobs.map((j) => j.type))).map((tp) => ({ id: tp, label: tp })),
    ],
    [localizedJobs, t.home.anyType],
  );

  const locations = useMemo(
    () => [
      { id: "ALL", label: t.home.allLocations },
      ...Array.from(new Set(localizedJobs.flatMap((j) => splitLocations(j.location)))).map((loc) => ({
        id: loc,
        label: loc,
      })),
    ],
    [localizedJobs, t.home.allLocations],
  );

  const filtered = localizedJobs.filter((j) =>
    (dept === "ALL" || j.dept === dept) &&
    (type === "ALL" || j.type === type) &&
    (location === "ALL" || splitLocations(j.location).includes(location)) &&
    (query === "" || (j.title + j.dept + j.location + j.desc).toLowerCase().includes(query.toLowerCase()))
  );

  const prevFilters = useRef({ dept, type, location, query, pageSize });
  useEffect(() => {
    const prev = prevFilters.current;
    const changed =
      prev.dept !== dept || prev.type !== type || prev.location !== location ||
      prev.query !== query || prev.pageSize !== pageSize;
    prevFilters.current = { dept, type, location, query, pageSize };
    if (changed) {
      setCurrentPage(1);
    }
  }, [dept, type, location, query, pageSize]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const indexOfLastJob = currentPage * pageSize;
  const indexOfFirstJob = indexOfLastJob - pageSize;
  const paginatedJobs = filtered.slice(indexOfFirstJob, indexOfLastJob);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (currentPage > 1 || pageSize !== DEFAULT_PAGE_SIZE) {
      params.set("page", String(currentPage));
      params.set("limit", String(pageSize));
    } else {
      params.delete("page");
      params.delete("limit");
    }
    const qs = params.toString();
    if (qs !== window.location.search.replace(/^\?/, "")) {
      window.history.replaceState(null, "", `${pathname}${qs ? `?${qs}` : ""}`);
    }
  }, [currentPage, pageSize, pathname]);

  useEffect(() => {
    if (currentPage > 1) {
      document.getElementById("openings")?.scrollIntoView({ block: "start" });
    }
  }, []);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    document.getElementById("openings")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-dvh bg-background font-sans text-foreground">
      <PortalHeader />
      <Hero jobCount={jobs.length} />
      <TrustBar />
      <main id="openings" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-10">
        <SectionHeading
          eyebrow={t.home.heroBadge}
          title={t.home.heroTitle}
          subtitle={t.home.heroSubtitle}
        />
        <SearchBar
          dept={dept} setDept={setDept} departments={departments}
          type={type} setType={setType} types={types}
          location={location} setLocation={setLocation} locations={locations}
          query={query} setQuery={setQuery}
        />
        {filtered.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              {t.home.showingResults.replace("{count}", String(filtered.length))}
            </span>
            <label className="flex items-center gap-2">
              <span>{t.home.itemsPerPage}:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-sm font-medium text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div className="mt-4 grid gap-5">
          {paginatedJobs.map((j) => (
            <JobCard key={j.id} job={j} applied={appliedIds.has(j.id)} checkingApplied={checkingApplied} />
          ))}
          {filtered.length === 0 && (
            <div className="rounded-xl border border-border bg-card p-12 text-center">
              <Search className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
              <p className="mt-3 font-medium">{t.home.noJobsTitle}</p>
              <p className="text-sm text-muted-foreground">{t.home.noJobsDesc}</p>
              <button
                type="button"
                onClick={() => {
                  setDept("ALL");
                  setType("ALL");
                  setLocation("ALL");
                  setQuery("");
                }}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary/10 px-4 py-2 text-xs font-semibold text-primary hover:bg-primary/20"
              >
                {t.home.resetFilters}
              </button>
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} />
        )}
      </main>
      <HowItWorks />
      <WhyMMMOCL />
      <PortalFooter />
    </div>
  );
}

/* ---------------- Sections ---------------- */

function Hero({ jobCount }: { jobCount: number }) {
  const { t } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);

  useEffect(() => {
    setIsLoggedIn(Boolean(getStoredCandidate()));
  }, []);

  return (
    <section className="relative isolate flex h-[calc(100dvh-4rem)] items-center overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <Image src={heroImage} alt="" fill priority className="object-cover" />
        <div className="absolute inset-0 bg-[linear-gradient(115deg,oklch(0.22_0.06_258/0.94)_0%,oklch(0.32_0.07_258/0.85)_45%,oklch(0.42_0.15_258/0.55)_100%)]" />
      </div>
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-success" /> {t.footer.copyright}
          </div>
          <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t.home.heroTitle}
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
            {t.home.heroSubtitle}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a href="#openings" className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-semibold text-navy shadow-[var(--shadow-elevated)] transition hover:-translate-y-0.5">
              {t.home.exploreOpenings} <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            {isLoggedIn === false && (
              <Link href="/login" className="inline-flex items-center gap-2 rounded-lg border border-white/25 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15">
                {t.header.signIn}
              </Link>
            )}
          </div>
          <dl className="mt-8 grid max-w-lg grid-cols-3 gap-6 border-t border-white/15 pt-5">
            {[
              { k: String(jobCount), v: t.home.statOpenings },
              { k: "1,200+", v: t.home.statApplicants },
              { k: "9", v: t.home.statLines },
            ].map((s) => (
              <div key={s.v}>
                <dt className="font-display text-2xl font-bold text-white sm:text-3xl">{s.k}</dt>
                <dd className="mt-1 text-xs font-medium uppercase tracking-wider text-white/60">{s.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const { t } = useLanguage();
  return (
    <div className="border-b border-border bg-card/60 py-4 backdrop-blur">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 text-xs font-semibold text-muted-foreground sm:px-6 lg:px-8">
        <span className="flex items-center gap-2 text-foreground">
          <Train className="h-4 w-4 text-primary" /> {t.footer.title}
        </span>
        <span>✓ {t.home.activeHiringBadge}</span>
      </div>
    </div>
  );
}

function SectionHeading({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle: string }) {
  return (
    <div className="max-w-2xl">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{eyebrow}</div>
      <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">{title}</h2>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function SearchBar({
  dept, setDept, departments, type, setType, types, location, setLocation, locations, query, setQuery,
}: {
  dept: string; setDept: (v: string) => void; departments: { id: string; label: string }[];
  type: string; setType: (v: string) => void; types: { id: string; label: string }[];
  location: string; setLocation: (v: string) => void; locations: { id: string; label: string }[];
  query: string; setQuery: (v: string) => void;
}) {
  const { t } = useLanguage();

  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-3 shadow-[var(--shadow-soft)]">
      <div className="grid gap-2 md:grid-cols-[1.2fr_1fr_1fr_1fr_auto]">
        <label className="relative block">
          <span className="sr-only">{t.home.searchPlaceholder}</span>
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t.home.searchPlaceholder}
            className="h-12 w-full rounded-xl border border-transparent bg-muted pl-10 pr-3 text-sm outline-none transition placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:ring-4 focus:ring-primary/15"
          />
        </label>
        <SelectField icon={Building2} value={dept} onChange={setDept} options={departments} label={t.jobDetails.deptLabel} />
        <SelectField icon={Briefcase} value={type} onChange={setType} options={types} label={t.jobDetails.typeLabel} />
        <SelectField icon={MapPin} value={location} onChange={setLocation} options={locations} label={t.jobDetails.locationLabel} />
        <button className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground transition hover:brightness-110">
          <Filter className="h-4 w-4" aria-hidden /> {t.home.searchBtn}
        </button>
      </div>
    </div>
  );
}

function SelectField({
  icon: Icon, value, onChange, options, label,
}: {
  icon: typeof Building2; value: string; onChange: (v: string) => void; options: { id: string; label: string }[]; label: string;
}) {
  return (
    <label className="relative block">
      <span className="sr-only">{label}</span>
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full appearance-none rounded-xl border border-transparent bg-muted pl-10 pr-9 text-sm font-medium outline-none transition focus:border-primary focus:bg-background focus:ring-4 focus:ring-primary/15"
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <ChevronRight className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-muted-foreground" aria-hidden />
    </label>
  );
}

function Pagination({
  currentPage, totalPages, onPageChange,
}: {
  currentPage: number; totalPages: number; onPageChange: (page: number) => void;
}) {
  const { t } = useLanguage();
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <nav aria-label="Openings pagination" className="mt-8 flex items-center justify-center gap-1.5">
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="inline-flex h-10 items-center gap-1 rounded-lg border border-border bg-card px-3 text-sm font-semibold text-navy transition hover:border-primary hover:text-primary disabled:pointer-events-none disabled:opacity-40"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> {t.home.paginationPrevious}
      </button>
      <div className="flex items-center gap-1">
        {pages.map((page) => (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            aria-current={page === currentPage ? "page" : undefined}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-lg text-sm font-semibold transition ${
              page === currentPage
                ? "bg-primary text-primary-foreground"
                : "border border-border bg-card text-navy hover:border-primary hover:text-primary"
            }`}
          >
            {page}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="inline-flex h-10 items-center gap-1 rounded-lg border border-border bg-card px-3 text-sm font-semibold text-navy transition hover:border-primary hover:text-primary disabled:pointer-events-none disabled:opacity-40"
      >
        {t.home.paginationNext} <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
    </nav>
  );
}

function JobCard({ job, applied, checkingApplied }: { job: Job; applied: boolean; checkingApplied: boolean }) {
  const { t } = useLanguage();
  const badgeStyle = job.tagColor === "success"
    ? "bg-success/10 text-success ring-success/20"
    : job.tagColor === "warning"
    ? "bg-warning/15 text-[oklch(0.5_0.16_60)] ring-warning/25"
    : "bg-primary/10 text-primary ring-primary/20";

  return (
    <article className="group rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[var(--shadow-elevated)] sm:p-7">
      <div className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-start">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-md bg-muted px-2 py-1 font-mono font-medium text-muted-foreground">{job.id}</span>
            {job.tag && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold ring-1 ring-inset ${badgeStyle}`}>
                {job.tag}
              </span>
            )}
          </div>
          <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-navy transition group-hover:text-primary sm:text-[22px]">
            {job.title}
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">{job.desc}</p>
          <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <Meta icon={Building2} label={job.dept} />
            <Meta icon={MapPin} label={job.location} />
            <Meta icon={Briefcase} label={job.type} />
            <Meta icon={GraduationCap} label={job.exp} />
            <Meta icon={Wallet} label={job.salary} />
            <Meta icon={Clock} label={`${t.home.applyBy}: ${job.deadline}`} />
          </dl>
        </div>
        <div className="flex flex-col gap-2 sm:w-44">
          {checkingApplied ? (
            <span className="h-11 w-full animate-pulse rounded-lg bg-muted" aria-hidden />
          ) : applied ? (
            <span className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-success/30 bg-success/10 px-4 text-sm font-semibold text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> {t.home.alreadyApplied}
            </span>
          ) : (
            <Link href={`/jobs/${job.id}/apply`} className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition hover:brightness-110">
              {t.home.applyNow} <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          )}
          <Link href={`/jobs/${job.id}`} className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-4 text-sm font-semibold text-navy transition hover:border-primary hover:text-primary">
            {t.home.viewDetails}
          </Link>
          {applied && (
            <Link href="/profile#applications" className="text-center text-xs font-semibold text-primary hover:underline">
              {t.apply.viewProfileBtn}
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

function Meta({ icon: Icon, label }: { icon: typeof Building2; label: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <dd className="truncate font-medium text-foreground/80">{label}</dd>
    </div>
  );
}

function HowItWorks() {
  const { t } = useLanguage();
  const steps = [
    { icon: UserPlus, title: t.home.step1Title, desc: t.home.step1Desc },
    { icon: FileText, title: t.home.step2Title, desc: t.home.step2Desc },
    { icon: Send, title: t.home.step3Title, desc: t.home.step3Desc },
  ];

  return (
    <section id="how" className="border-y border-border bg-muted/50 py-8 sm:py-10 lg:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t.header.howToApply}
          title={t.home.howToTitle}
          subtitle={t.home.howToSubtitle}
        />
        <ol className="mt-10 grid gap-5 sm:grid-cols-3">
          {steps.map((s, i) => (
            <li key={s.title} className="relative rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-soft)]">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                <s.icon className="h-5 w-5" aria-hidden />
              </div>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">0{i + 1}</div>
              <h3 className="mt-1 font-display text-lg font-bold text-navy">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function WhyMMMOCL() {
  const { t } = useLanguage();
  const perks = [
    { icon: Shield, title: t.home.benefit1Title, desc: t.home.benefit1Desc },
    { icon: TrendingUp, title: t.home.benefit2Title, desc: t.home.benefit2Desc },
    { icon: Users, title: t.home.benefit3Title, desc: t.home.benefit3Desc },
    { icon: Award, title: t.home.benefit4Title, desc: t.home.benefit4Desc },
  ];

  return (
    <section id="why" className="py-8 sm:py-10 lg:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t.header.lifeAtMMMOCL}
          title={t.home.whyJoinTitle}
          subtitle={t.home.whyJoinSubtitle}
        />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-elevated)]">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--gradient-hero)] text-white">
                <p.icon className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-navy">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
